import { markRaw, reactive } from 'vue';
import { Auth0Client } from '@auth0/auth0-spa-js';

let instance;

/**
 * auth0-spa-js 1.x cuts each query value at its first "=" and vue-router 4
 * leaves the base64 padding of `state` unencoded: the state no longer matches
 * the stored transaction ("Invalid state"). URLSearchParams re-encodes it.
 */
const callbackUrl = () => {
  const { origin, pathname, search } = window.location;

  return `${origin}${pathname}?${new URLSearchParams(search).toString()}`;
};

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
const useAuth0 = ({
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = reactive({
    loading: true,
    isAuthenticated: false,
    user: {},
    auth0Client: null,
    popupOpen: false,
    error: null,
    /** Handles the callback when logging in using a redirect */
    async handleRedirectCallback() {
      this.loading = true;
      try {
        await this.auth0Client.handleRedirectCallback(callbackUrl());
        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
        this.error = null;
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Creates and memoizes the Auth0 client. The client is built
     * with the constructor rather than `createAuth0Client()`, which also runs
     * `checkSession()`: a hidden-iframe silent authentication on every page
     * load. The app only uses Auth0 for the social login redirect, so that
     * call is useless, and on Safari (ITP blocks the third-party iframe) it
     * hangs 60 seconds then rejects with an unhandled "Timeout" error
     * (SP-14334 / Sentry CUSTOMER-5DP).
     */
    ensureAuth0Client() {
      if (!this.auth0Client) {
        this.auth0Client = markRaw(new Auth0Client({
          ...options,
          client_id: options.clientId,
          redirect_uri: redirectUri,
        }));
      }

      return this.auth0Client;
    },
    /** Authenticates the user using the redirect method */
    async loginWithRedirect(o) {
      const client = this.ensureAuth0Client();

      return client.loginWithRedirect(o);
    },
    /** Returns all the claims present in the ID token */
    getIdTokenClaims(o) {
      return this.auth0Client.getIdTokenClaims(o);
    },
    /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
    getTokenSilently(o) {
      return this.auth0Client.getTokenSilently(o);
    },
    /** Gets the access token using a popup window */

    getTokenWithPopup(o) {
      return this.auth0Client.getTokenWithPopup(o);
    },
    /** Logs the user out and removes their session on the authorization server */
    logout(o) {
      return this.auth0Client.logout(o);
    },
    async connectUserAndGetToken() {
      this.ensureAuth0Client();

      try {
        // handle the redirect and retrieve tokens
        await this.auth0Client.handleRedirectCallback(callbackUrl());
      } catch (e) {
        this.error = e;

        window.console.error('error handle redirect callback', e);

        return null;
      }
      // Initialize our internal authentication state
      this.isAuthenticated = await this.auth0Client.isAuthenticated();
      this.user = await this.auth0Client.getUser();

      return this.auth0Client.getIdTokenClaims();
    },
  });
  instance.ensureAuth0Client();

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
const Auth0Plugin = {
  install(app, options) {
    Object.assign(app.config.globalProperties, { $auth0: useAuth0(options) });
  },
};

export default Auth0Plugin;
